<script setup lang="ts">
    import { useEditCoachingFormStore } from '@/js/stores/edit-coaching-form';
    import CheckboxInput from '../CheckboxInput.vue';
    import FormGroup from '../FormGroup.vue';
    import { storeToRefs } from 'pinia';
    import { watch } from 'vue';
    import TextInput, { TextInputWidth } from '../TextInput.vue';
    import ToggleSwitch from '../ToggleSwitch.vue';
    import IconButton from '../IconButton.vue';
    import InputLabel from '../InputLabel.vue';
    import { __ } from '@/js/stores/expressions';
    import SelectInput from '../SelectInput.vue';
    import { useElementFocus } from '@/js/composables/useElementFocus';
    import EchoButton from '../EchoButton.vue';
    import { PlusIcon, XIcon } from '@lucide/vue';

    const { addApprovalRating, removeApprovalRating } = useEditCoachingFormStore();
    const { formDraft, formPoints } = storeToRefs(useEditCoachingFormStore());

    const { setFocus } = useElementFocus();

    watch(
        () => formDraft.value?.useApprovalRating,
        () => {
            if (!formDraft.value?.useApprovalRating) {
                delete formDraft.value?.approvalRatings;
            } else {
                formDraft.value.approvalRatings = {
                    criticalQuestionFallbackRatingId: '',
                    ratings: [],
                };
            }
        }
    );

    const addApprovalRatingAndSetFocus = () => {
        const approvalRating = addApprovalRating();
        setFocus(`#approval-rating-name-${approvalRating.id}`);
    };
</script>

<template>
    <div v-if="formDraft">
        <FormGroup :help-hint="__('explain', { label: __('approval-rating') })">
            <template #header>
                {{ __('approval-rating') }}
            </template>
            <template #help>
                <p>
                    {{ __('approval-rating-description') }}
                </p>
            </template>
            <CheckboxInput id="form-approval-rating" v-model="formDraft.useApprovalRating">
                {{ __('approval-rating-add-rating') }}
            </CheckboxInput>
        </FormGroup>
        <div
            v-if="formDraft.useApprovalRating && formDraft.approvalRatings"
            class="bg-gray-extralight p-6 rounded-md mt-4"
        >
            <FormGroup>
                <div
                    class="gap-x-6 gap-y-2.5 grid [grid-template-columns:auto_minmax(max-content,auto)_auto_auto] items-center"
                >
                    <div class="uppercase text-sm font-medium mb-1 block">
                        {{ __('approval-rating-name') }}
                    </div>
                    <div class="uppercase text-sm font-medium mb-1 block">
                        {{ __('points-range') }}
                    </div>
                    <div class="uppercase text-sm font-medium mb-1 block">
                        {{ __('approval-rating-require-approval-before-display') }}
                    </div>
                    <div />

                    <template v-for="rating in formDraft.approvalRatings.ratings" :key="rating.id">
                        <div>
                            <TextInput
                                :id="`approval-rating-name-${rating.id}`"
                                v-model="rating.name"
                                :aria-label="__('approval-rating-name')"
                                :width="TextInputWidth.Medium"
                            />
                        </div>
                        <div class="flex items-center gap-1.5">
                            <TextInput
                                v-model.number="rating.minPoints"
                                :aria-label="__('approval-rating-min-points')"
                                type="number"
                                :width="TextInputWidth.Tiny"
                            />
                            -
                            <TextInput
                                v-model.number="rating.maxPoints"
                                :aria-label="__('approval-rating-max-points')"
                                type="number"
                                :width="TextInputWidth.Tiny"
                            />
                            <span class="block whitespace-nowrap">
                                {{
                                    __('approval-rating-of-total-points', {
                                        total: String(formPoints),
                                    })
                                }}
                            </span>
                        </div>
                        <div>
                            <ToggleSwitch
                                v-model="rating.requireApprovalBeforeDisplay"
                                :aria-label="__('approval-rating-require-approval-before-display')"
                                :label="__('require-approval')"
                            />
                        </div>
                        <div>
                            <IconButton
                                class="text-gray-medium hover:bg-gray-lightest hover:text-error"
                                area-size="size-8"
                                size="size-4"
                                :aria-label="__('remove')"
                                @click="removeApprovalRating(rating.id)"
                            >
                                <XIcon class="size-4" />
                            </IconButton>
                        </div>
                    </template>
                </div>
            </FormGroup>
            <div class="mt-2 mb-6">
                <EchoButton
                    id="add-approval-rating-button"
                    variant="dashed"
                    size="small"
                    @click="addApprovalRatingAndSetFocus()"
                >
                    <template #icon-left><PlusIcon /></template>
                    {{ __('approval-rating-add-rating') }}
                </EchoButton>
            </div>
            <FormGroup>
                <div>
                    <InputLabel for="form-critical-question-fallback-rating">
                        {{ __('approval-rating-when-zero-points') }}
                    </InputLabel>
                    <SelectInput
                        id="form-critical-question-fallback-rating"
                        v-model="formDraft.approvalRatings.criticalQuestionFallbackRatingId"
                    >
                        <option value="">
                            {{ __('approval-rating-select') }}
                        </option>
                        <option
                            v-for="rating in formDraft.approvalRatings.ratings"
                            :key="rating.id"
                            :value="rating.id"
                        >
                            {{ rating.name }}
                        </option>
                    </SelectInput>
                </div>
            </FormGroup>
        </div>
    </div>
</template>
